// src/redux/checkout.js
// ─────────────────────────────────────────────────────────────────────────────
// SHARED order-placement helpers for the web checkout screens — the ONE
// implementation of the "order-first, then pay" contract (Playbook 12
// §B3/§C4). Before this, each of the five place-order screens re-implemented
// the same "await createOrder → check requestStatus → read the new order's id →
// classify a 422" dance inline. This centralises it so every screen behaves
// identically and a new screen gets it for free.
// ─────────────────────────────────────────────────────────────────────────────

// 422 codes the customer can recover from by changing their delivery address.
export const OUT_OF_RANGE_CODES = ["OUT_OF_DELIVERY_RANGE", "VENDOR_COORDS_MISSING"];

/**
 * Pull the created order's id out of a createOrder thunk `action`, tolerating
 * the shapes the backend has returned: { order: { _id } } | { _id } | { orderId }.
 * @returns {string|null}
 */
export function extractCreatedOrderId(action) {
  const p = action?.payload;
  return p?.order?._id || p?._id || p?.orderId || null;
}

/**
 * THE amount to charge for a created order: its server totalPrice as the
 * order create answer returned it (items re-priced, the delivery fee, and any
 * payment processing fee the server passes on). Every web rail (MoMo, Airtel
 * and the other Flutterwave mobile money, card) charges this and nothing
 * else. Never a sum the screen adds up: the pre-charge guard
 * (verifyChargeAmount) refuses any figure but the order's own total, so a
 * client sum only ever matched while no processing fee was passed on.
 *
 * Reads a createOrder thunk `action` (its payload is the order, or
 * { order }), an order, or a total already read from one.
 * @returns {number|null} null when there is no usable total: do not charge.
 */
export function orderChargeAmount(source) {
  const answer = source && typeof source === "object" && "payload" in source ? source.payload : source;
  const raw =
    answer !== null && typeof answer === "object"
      ? answer.order?.totalPrice ?? answer.totalPrice
      : answer;
  if (raw === null || raw === undefined || raw === "") return null;
  const total = Number(raw);
  return Number.isFinite(total) && total > 0 ? total : null;
}

/** Said when the created order came back without a total: nothing is charged. */
export const ORDER_TOTAL_UNREADABLE =
  "We could not read your order's total, so nothing has been charged. Your order is saved: open it in My Orders to pay for it.";

/**
 * Create the order and return a NORMALISED outcome — the shared decision point
 * for every web pay handler. It does not show toasts, clear carts, or redirect;
 * the caller does that per screen (keeping this pure/testable).
 *
 * The web createOrder thunks use apisauce-style thunks that `rejectWithValue`
 * on !ok, so a failed create surfaces as `action.error` or
 * `requestStatus !== "fulfilled"` with the backend body in `action.payload`.
 *
 * @param {object}   params
 * @param {Function} params.dispatch      redux dispatch
 * @param {Function} params.createOrder   the createAsyncThunk for this surface
 * @param {object}   params.payload       order-create body
 * @returns {Promise<{ ok, orderId, outOfRange, code, message, action }>}
 */
export async function createOrderFirst({ dispatch, createOrder, payload }) {
  const action = await dispatch(createOrder(payload));
  const orderId = extractCreatedOrderId(action);
  const fulfilled = !action?.error && action?.meta?.requestStatus === "fulfilled";

  if (fulfilled) {
    return { ok: true, orderId, outOfRange: false, code: null, message: null, action };
  }
  const code = action?.payload?.code || null;
  return {
    ok: false,
    orderId: null,
    outOfRange: OUT_OF_RANGE_CODES.includes(code),
    code,
    message: action?.payload?.message || null,
    action,
  };
}

const checkout = {
  OUT_OF_RANGE_CODES,
  ORDER_TOTAL_UNREADABLE,
  extractCreatedOrderId,
  orderChargeAmount,
  createOrderFirst,
};
export default checkout;
